import startOfDay from 'date-fns/startOfDay';
import subYears from 'date-fns/subYears';
import getUnixTimestamp from '@/Framework/dateHelpers/getUnixTimestamp';
import getUTCDate from '@/Framework/dateHelpers/getUTCDate';
import getLocalDate from '@/Framework/dateHelpers/getLocalDate';
import { DEFAULT_TTM_SELECT_VALUE } from './constants';
import {
  IPeriodFilter, IScorecardCompanyData,
  IScorecardData, IScorecardState,
  ITtmFilter,
  ITtmFilterResponse,
} from '@/finsight/application/League/interfaces';
import { REDIRECT_ERROR_URL_WRONG_ID } from '@/finsight/application/actions/redirect/constants';
import map from 'lodash/map';
import filter from 'lodash/filter';
import { isChangedProductsRegions } from '@/finsight/application/helpers';
import { addQueryParams, getQueryString } from '@/Framework/Router/nextRouter';

export const defaultResponse = { collection: [], totalCount: 0 };

export const defaultTtmFilter = {
  disabled: false,
  dateFrom: getUnixTimestamp(getLocalDate(startOfDay(subYears(getUTCDate(), 1)))),
  dateTo: null,
  label: DEFAULT_TTM_SELECT_VALUE,
  value: DEFAULT_TTM_SELECT_VALUE,
};

export const defaultTtmFilterResponse = {
  active: true,
  dateFrom: getUnixTimestamp(getLocalDate(startOfDay(subYears(getUTCDate(), 1)))),
  dateTo: null,
  filterName: DEFAULT_TTM_SELECT_VALUE,
};

export const mapGetPeriodsResult = (result:ITtmFilterResponse[]):ITtmFilter[] => result.map(
  (filter) => (
    {
      value: filter.filterName,
      label: filter.filterName,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      disabled: !filter.active,
    }
  ),
);

export const combineReducers = (slices) => (state, action) => Object.keys(slices).reduce(
    (acc, prop) => ({
      ...acc,
      [prop]: slices[prop](acc[prop], action),
    }),
    state,
  );

export const getTtmFilterInitialState = (ttmFilter:ITtmFilter[]):IPeriodFilter => {
  const collection = ttmFilter.length > 0 ? ttmFilter : [defaultTtmFilter];
  const selected = collection.find((filter) => filter.value === DEFAULT_TTM_SELECT_VALUE) || defaultTtmFilter;

  return {
    isCollectionFetching: false,
    collection,
    selected,
  };
};

interface IScorecardInitialStateProps {
  scorecardData: IScorecardData,
  companyData: IScorecardCompanyData,
}

export const getScorecardInitialState = ({
  scorecardData,
  companyData,
}:IScorecardInitialStateProps):IScorecardState => ({
  data: scorecardData,
  isFetching: false,
  initialized: !!Object.keys(scorecardData).length,
  expandedCategories: [],
  columnState: {
    valueTableIndex: 0,
    valueTableColumnCount: 4,
    rankTableIndex: 0,
    rankTableColumnCount: 4,
  },
  selectedPeriod: {
    categoryId: null,
    subCategoryId: null,
    name: '',
    dateFrom: null,
    dateTo: null,
  },
  company: {
    data: { ...companyData },
    error: companyData.id ? null : REDIRECT_ERROR_URL_WRONG_ID,
    isCompanyFetching: false,
  },
});

interface IRegionsAndProducts {
  productIds: string[],
  regionIds: string[],
}

interface IRedirect {
  redirect: {
    permanent: boolean,
    destination: string,
  },
}

export const getRegionsAndProducts = (context):IRegionsAndProducts | IRedirect => {
  const { query, productsRegions, dictionaries } = context;
  const { products, regions } = query;
  const { regionsList, productsList } = dictionaries;
  const { productIds, regionIds } = productsRegions;
  const validProducts = map(filter(productsList, (item) => productIds.includes(item.id)), 'abbreviation');
  const validRegions = map(filter(regionsList, (item) => regionIds.includes(item.id)), 'abbreviation');

  if (isChangedProductsRegions(regions, products, validRegions, validProducts)) {
    const { tab, leagueType, viewId, companyName, ...restParams } = query;
    const newQuery = addQueryParams(restParams, { products: validProducts, regions: validRegions });

    return {
      redirect: {
        permanent: false,
        destination: `?${ getQueryString(newQuery) }`,
      },
    };
  }

  return {
    productIds,
    regionIds,
  };
};

export const getDefaultResponse = ({ page, perPage, response, ...other }) => ({
  data: response.collection,
  isFetching: false,
  totalCount: response.totalCount,
  page,
  perPage,
  ...other,
});
